import { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Box, Button, Paper, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import { GreenCheckbox } from "../notifications/filters/GreenCheckBox";
import {
  ConfigurationDetailKeys,
  defaultFormValues,
  DurationPicker,
  CountPicker,
  TimeTriggerPicker,
  ThresholdPicker,
  IConfigEventForm,
} from "./EventConfigForm";
import { updateUnitConfigByEventType } from "./settingsThunks";
import { TimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import LuxonUtils from "@date-io/luxon";
import {
  ConfigLevelTwo,
  EventsWithBloodPressureConfig,
  EventsWithCountConfig,
  EventsWithDurationConfig,
  EventsWithTimeConfig,
  EventsWithTriggerTime,
  getBloodPressureConfigBounds,
  ISO_TIME_SAVE_FORMAT,
  RoleTypes,
  TimeFormat,
  i18textByEventType,
} from "../../helpers/constants";
import { DateTime } from "luxon";
import {
  IUnitEventConfigData,
  UnitEventTypeConfig,
} from "../../services/settings.services";
import {
  convertTimeHourMinute,
  DateTimeNow,
  DateTimeNowHourMinute,
  loadTime,
  timeConfigurations,
} from "../../helpers/datetime";
import { showSoftNotification } from "../app/appSlice";
import { SoftNotificationTypes } from "../common/AppSnack";
import { numberRange } from "./common/helpers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(4),
      height: "100%",
      overflowY: "auto",
      width: "550px",
    },
    title: {
      fontFamily: theme.typography.tertiaryFontFamily,
      fontSize: theme.typography.h5.fontSize,
      marginBottom: theme.spacing(3),
      textAlign: "center",
    },
    section: {
      border: `0.5px solid ${theme.palette.dark.light}`,
      borderRadius: "5px",
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.body1.fontSize,
    },
    sectionDescription: {
      fontSize: theme.typography.body2.fontSize,
      marginTop: theme.spacing(1),
      whiteSpace: "pre-line",
    },
    configurationDetail: {
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.4,
      marginBottom: theme.spacing(2),
      whiteSpace: "pre-line",
    },
    configSectionTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.body1.fontSize,
      marginBottom: theme.spacing(1),
    },
    row: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(2),
      "&:last-child": {
        marginBottom: 0,
      },
    },
    rowControl: {
      marginLeft: "auto",
    },
    rowControlNarrow: {
      marginLeft: "auto",
      width: "80px",
      "& .MuiAutocomplete-root": {
        width: "80px",
        minWidth: "unset",
      },
    },
    rowLabel: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.body2.fontSize,
    },
    timePicker: {
      fontSize: theme.typography.body2.fontSize,
      width: "140px",
    },
    saveButtonContainer: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: theme.spacing(2),
    },
  })
);

export const AipNotificationSettings = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  /* Selectors */
  const selectedEventType = useAppSelector(
    (state) => state.settingsState.selectedEventType
  );

  const unitsEventConfig = useAppSelector(
    (state) => state.settingsState.unitsEventConfig
  );

  const selectedUnit = useAppSelector(
    (state) => state.settingsState.selectedUnit
  );

  const community = useAppSelector(
    (state) => state.headerState.selectedCommunity
  );

  const isCaregiver = useAppSelector(
    (state) =>
      !!state.headerState.selectedCommunity?.role.includes(RoleTypes.caregiver)
  );

  const is24HourTime = useAppSelector(
    (state) => state.appState.config.timeFormat === TimeFormat.TIME24H
  );

  const { timeFormat } = timeConfigurations();

  const eventType = selectedEventType ?? "";
  const unitConfig: UnitEventTypeConfig | undefined = unitsEventConfig?.find(
    (config) =>
      config.unit_id === selectedUnit && config.event_type === eventType
  );

  const hasDuration = EventsWithDurationConfig.includes(eventType);
  const hasCount = EventsWithCountConfig.includes(eventType);
  const hasTime = EventsWithTimeConfig.includes(eventType);
  const hasTrigger = EventsWithTriggerTime.includes(eventType);
  const hasBloodPressure = EventsWithBloodPressureConfig.includes(eventType);
  const hasConfiguration =
    hasDuration || hasCount || hasTime || hasTrigger || hasBloodPressure;
  const configurationDetailKey = ConfigurationDetailKeys[eventType];

  /* State */
  const [eventConfig, setEventConfig] = useState<IConfigEventForm | undefined>(
    undefined
  );

  /* Effects */
  useEffect(() => {
    if (unitConfig !== undefined && eventType) {
      setEventConfig(defaultFormValues(unitConfig, eventType));
    }
  }, [unitConfig, eventType]);

  /* Handlers */
  const handleEnabledChange = async () => {
    if (!eventConfig || !eventType || !selectedUnit) return;
    const newEnabled = !eventConfig.is_enabled;
    setEventConfig((prev) =>
      prev ? { ...prev, is_enabled: newEnabled } : prev
    );
    await dispatch(
      updateUnitConfigByEventType({
        eventType: eventType,
        unitIDs: [selectedUnit],
        is_enabled: newEnabled,
      })
    );
  };

  const handlePeriodChange = (value: number | null) => {
    setEventConfig((prev) => (prev ? { ...prev, duration: value } : prev));
  };

  const handleCountChange = (value: number | null) => {
    setEventConfig((prev) => (prev ? { ...prev, count: value } : prev));
  };

  const handleAllDayChange = () => {
    setEventConfig((prev) =>
      prev ? { ...prev, all_day: !prev.all_day } : prev
    );
  };

  const handleStartTimeChange = (date: DateTime | null) => {
    setEventConfig((prev) =>
      prev ? { ...prev, local_time_start: date } : prev
    );
  };

  const handleEndTimeChange = (date: DateTime | null) => {
    setEventConfig((prev) => (prev ? { ...prev, local_time_end: date } : prev));
  };

  const handleTriggerEndTimeChange = (date: DateTime | null) => {
    const hour = date?.hour;
    const minHour = 7;
    const maxHour = 14;
    if (hour !== undefined && hour >= minHour && hour <= maxHour) {
      setEventConfig((prev) =>
        prev ? { ...prev, local_time_end: date } : prev
      );
    } else {
      const formatInitial = convertTimeHourMinute(
        DateTimeNow().set({ hour: minHour, minute: 0, second: 0 }),
        timeFormat
      );
      const formatfinal = convertTimeHourMinute(
        DateTimeNow().set({ hour: maxHour, minute: 0, second: 0 }),
        timeFormat
      );
      dispatch(
        showSoftNotification(
          t("trigger_time_range_validation", {
            initial: formatInitial,
            final: formatfinal,
          })
        )
      );
    }
  };

  const handleThresholdChange = (field: keyof IConfigEventForm) => (
    value: number | null
  ) => {
    setEventConfig((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveConfiguration = async () => {
    if (!eventConfig || !eventType || !selectedUnit) return;

    const configData: IUnitEventConfigData = {
      eventType: eventType,
      unitIDs: [selectedUnit],
    };

    const configToUpdate: IUnitEventConfigData["configuration"] = {};

    if (hasDuration && eventConfig.duration) {
      configToUpdate.duration = eventConfig.duration;
      configToUpdate.level = ConfigLevelTwo;
    }
    if (hasCount && eventConfig.count) {
      configToUpdate.count = eventConfig.count;
      configToUpdate.level = ConfigLevelTwo;
    }

    const allDay = !!eventConfig.all_day;

    if (hasTime && eventConfig.all_day !== undefined) {
      configToUpdate.all_day = eventConfig.all_day;
      configToUpdate.level = ConfigLevelTwo;
    }
    if (hasTime && !allDay && eventConfig.local_time_start !== undefined) {
      configToUpdate.local_time_start = convertTimeHourMinute(
        eventConfig.local_time_start,
        ISO_TIME_SAVE_FORMAT,
        community?.time_zone
      );
      configToUpdate.level = ConfigLevelTwo;
    }
    if (hasTime && !allDay && eventConfig.local_time_end) {
      configToUpdate.local_time_end = convertTimeHourMinute(
        eventConfig.local_time_end,
        ISO_TIME_SAVE_FORMAT,
        community?.time_zone
      );
      configToUpdate.level = ConfigLevelTwo;
    }
    if (hasTrigger && eventConfig.local_time_end) {
      configToUpdate.local_time_end = eventConfig.local_time_end.toFormat(
        ISO_TIME_SAVE_FORMAT
      );
      configToUpdate.level = ConfigLevelTwo;
    }
    if (hasBloodPressure) {
      if (eventConfig.systolic_high != null) {
        configToUpdate.systolic_high = eventConfig.systolic_high;
        configToUpdate.level = ConfigLevelTwo;
      }
      if (eventConfig.systolic_low != null) {
        configToUpdate.systolic_low = eventConfig.systolic_low;
        configToUpdate.level = ConfigLevelTwo;
      }
      if (eventConfig.diastolic_high != null) {
        configToUpdate.diastolic_high = eventConfig.diastolic_high;
        configToUpdate.level = ConfigLevelTwo;
      }
      if (eventConfig.diastolic_low != null) {
        configToUpdate.diastolic_low = eventConfig.diastolic_low;
        configToUpdate.level = ConfigLevelTwo;
      }
    }

    if (Object.keys(configToUpdate).length > 0) {
      configData.configuration = configToUpdate;
    }

    const result = await dispatch(updateUnitConfigByEventType(configData));
    if (updateUnitConfigByEventType.fulfilled.match(result)) {
      dispatch(
        showSoftNotification(SoftNotificationTypes.SAVED_CONFIG_CONFIRMATION)
      );
    }
  };

  const hasConfigChanges = () => {
    if (!eventConfig || !unitConfig) return false;
    const defaults = defaultFormValues(unitConfig, eventType);

    if (defaults.duration !== eventConfig.duration) return true;
    if (defaults.count !== eventConfig.count) return true;
    if (defaults.all_day !== eventConfig.all_day) return true;
    if (
      defaults.local_time_start?.toISO() !==
      eventConfig.local_time_start?.toISO()
    )
      return true;
    if (
      defaults.local_time_end?.toISO() !== eventConfig.local_time_end?.toISO()
    )
      return true;
    if (defaults.systolic_high !== eventConfig.systolic_high) return true;
    if (defaults.systolic_low !== eventConfig.systolic_low) return true;
    if (defaults.diastolic_high !== eventConfig.diastolic_high) return true;
    if (defaults.diastolic_low !== eventConfig.diastolic_low) return true;

    return false;
  };

  if (!selectedEventType || !eventConfig) return null;

  const notificationName =
    i18textByEventType(eventType) !== "-"
      ? t(i18textByEventType(eventType))
      : eventType;

  const allDayStartDateTime = DateTimeNowHourMinute("00:00");
  const allDayEndDateTime = DateTimeNowHourMinute("0:00");

  return (
    <Paper className={classes.container} elevation={0}>
      <Typography className={classes.title}>{notificationName}</Typography>

      {/* Send App Notifications Section */}
      <Box className={classes.section}>
        <Box className={classes.row}>
          <Typography className={classes.sectionTitle}>
            {t("send_app_notifications")}
          </Typography>
          <GreenCheckbox
            className={classes.rowControl}
            checked={eventConfig.is_enabled}
            tabIndex={-1}
            disableRipple
            onClick={handleEnabledChange}
          />
        </Box>
        <Typography className={classes.sectionDescription}>
          {t("push_notification_description", {
            eventName: notificationName,
          })}
        </Typography>
      </Box>

      {/* Configuration Section */}
      {hasConfiguration && !isCaregiver && (
        <>
          <Typography className={classes.configSectionTitle}>
            {t("configuration")}
          </Typography>
          <Box className={classes.section}>
            {configurationDetailKey && (
              <Typography className={classes.configurationDetail}>
                {t(configurationDetailKey)}
              </Typography>
            )}
            {hasDuration && (
              <Box className={classes.row}>
                <Typography className={classes.rowLabel}>
                  {t("period")}
                </Typography>
                <Box className={classes.rowControl}>
                  <DurationPicker
                    value={eventConfig.duration}
                    updateValue={handlePeriodChange}
                    eventType={eventType}
                  />
                </Box>
              </Box>
            )}

            {hasCount && (
              <Box className={classes.row}>
                <Typography className={classes.rowLabel}>
                  {t("count")}
                </Typography>
                <Box className={classes.rowControlNarrow}>
                  <CountPicker
                    value={eventConfig.count}
                    updateValue={handleCountChange}
                    eventType={eventType}
                  />
                </Box>
              </Box>
            )}

            {hasTime && (
              <>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("all_day")}
                  </Typography>
                  <GreenCheckbox
                    className={classes.rowControl}
                    checked={eventConfig.all_day}
                    tabIndex={-1}
                    disableRipple
                    onClick={handleAllDayChange}
                  />
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("start_time")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <MuiPickersUtilsProvider utils={LuxonUtils}>
                      <TimePicker
                        ampm={!is24HourTime}
                        disabled={eventConfig.all_day}
                        value={loadTime(
                          eventConfig.all_day
                            ? allDayStartDateTime
                            : eventConfig.local_time_start ?? null
                        )}
                        format={timeFormat}
                        onChange={handleStartTimeChange}
                        minutesStep={5}
                        className={classes.timePicker}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("end_time")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <MuiPickersUtilsProvider utils={LuxonUtils}>
                      <TimePicker
                        ampm={!is24HourTime}
                        disabled={eventConfig.all_day}
                        value={loadTime(
                          eventConfig.all_day
                            ? allDayEndDateTime
                            : eventConfig.local_time_end ?? null
                        )}
                        format={timeFormat}
                        onChange={handleEndTimeChange}
                        minutesStep={5}
                        className={classes.timePicker}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>
                </Box>
              </>
            )}

            {hasTrigger && (
              <Box className={classes.row}>
                <Typography className={classes.rowLabel}>
                  {t("trigger_time")}
                </Typography>
                <Box className={classes.rowControl}>
                  <TimeTriggerPicker
                    value={
                      eventConfig.local_time_end
                        ? eventConfig.local_time_end.hour +
                          eventConfig.local_time_end.minute / 60
                        : 0
                    }
                    updateValue={handleTriggerEndTimeChange}
                    eventType={eventType}
                  />
                </Box>
              </Box>
            )}

            {hasBloodPressure && (
              <>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("systolic_high")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <ThresholdPicker
                      value={eventConfig.systolic_high ?? null}
                      options={numberRange(
                        getBloodPressureConfigBounds().systolic.min,
                        getBloodPressureConfigBounds().systolic.max,
                        getBloodPressureConfigBounds().systolic.step
                      )}
                      updateValue={handleThresholdChange("systolic_high")}
                    />
                  </Box>
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("systolic_low")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <ThresholdPicker
                      value={eventConfig.systolic_low ?? null}
                      options={numberRange(
                        getBloodPressureConfigBounds().systolic.min,
                        getBloodPressureConfigBounds().systolic.max,
                        getBloodPressureConfigBounds().systolic.step
                      )}
                      updateValue={handleThresholdChange("systolic_low")}
                    />
                  </Box>
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("diastolic_high")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <ThresholdPicker
                      value={eventConfig.diastolic_high ?? null}
                      options={numberRange(
                        getBloodPressureConfigBounds().diastolic.min,
                        getBloodPressureConfigBounds().diastolic.max,
                        getBloodPressureConfigBounds().diastolic.step
                      )}
                      updateValue={handleThresholdChange("diastolic_high")}
                    />
                  </Box>
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.rowLabel}>
                    {t("diastolic_low")}
                  </Typography>
                  <Box className={classes.rowControl}>
                    <ThresholdPicker
                      value={eventConfig.diastolic_low ?? null}
                      options={numberRange(
                        getBloodPressureConfigBounds().diastolic.min,
                        getBloodPressureConfigBounds().diastolic.max,
                        getBloodPressureConfigBounds().diastolic.step
                      )}
                      updateValue={handleThresholdChange("diastolic_low")}
                    />
                  </Box>
                </Box>
              </>
            )}

            <Box className={classes.saveButtonContainer}>
              <Button
                color="primary"
                size="small"
                disabled={!hasConfigChanges()}
                onClick={handleSaveConfiguration}
              >
                {t("save")}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};
